import { clampLayerFrame } from '@/lib/layers/clampLayerFrame';
import { clampTextCenterPx } from './quoteTextLayout';
import type { QuoteBackgroundFrame, QuoteSlide, QuoteTextPosition } from './types';

/** Slide geometry the drag math needs, in composition px (from
 *  `computeQuoteTextLayout`). */
export interface SlidePlacementGeometry {
  width: number;
  height: number;
  padding: number;
  block: { left: number; top: number; width: number; height: number };
}

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

/** New text center after a drag of (dxFrac, dyFrac) — fractions of the
 *  slide — from `center`, clamped so the block stays inside the padding. */
export function applyTextDrag(args: SlidePlacementGeometry & {
  center: QuoteTextPosition;
  dxFrac: number;
  dyFrac: number;
}): QuoteTextPosition {
  const {
    center, dxFrac, dyFrac, width, height, padding, block,
  } = args;
  const px = clampTextCenterPx({
    center: { x: center.x + dxFrac, y: center.y + dyFrac },
    blockWidth: block.width,
    blockHeight: block.height,
    width,
    height,
    padding,
  });
  return { x: clamp01(px.x / width), y: clamp01(px.y / height) };
}

/** Whether a point (fractions of the slide) lies on the text block. */
export function isPointInTextBlock(point: QuoteTextPosition, geometry: SlidePlacementGeometry): boolean {
  const x = point.x * geometry.width;
  const y = point.y * geometry.height;
  const { block } = geometry;
  return x >= block.left && x <= block.left + block.width && y >= block.top && y <= block.top + block.height;
}

/** The background frame with a new zoom (clamped 1–3), keeping its pan. */
export function withBackgroundZoom(frame: QuoteBackgroundFrame | undefined, scale: number): QuoteBackgroundFrame {
  return clampLayerFrame({
    scale,
    offsetX: frame?.offsetX ?? 0,
    offsetY: frame?.offsetY ?? 0,
  });
}

/** "Restaurar posição": back to the style's default text layout and a
 *  centered cover background. */
export function resetSlidePlacement(slide: QuoteSlide): QuoteSlide {
  const rest = { ...slide };
  delete rest.textPosition;
  delete rest.backgroundFrame;
  return rest;
}

export function sanitizeTextPosition(raw: unknown): QuoteTextPosition | null {
  if (raw === null || typeof raw !== 'object') return null;
  const { x, y } = raw as Record<string, unknown>;
  if (!isFiniteNumber(x) || !isFiniteNumber(y)) return null;
  return { x: clamp01(x), y: clamp01(y) };
}

export function sanitizeBackgroundFrame(raw: unknown): QuoteBackgroundFrame | null {
  if (raw === null || typeof raw !== 'object') return null;
  const { scale, offsetX, offsetY } = raw as Record<string, unknown>;
  if (!isFiniteNumber(scale) || !isFiniteNumber(offsetX) || !isFiniteNumber(offsetY)) return null;
  return clampLayerFrame({ scale, offsetX, offsetY });
}
